import { useEffect, useMemo, useState } from "react";
import { getRecentRawImageEntry } from "./installationConfigUtils";
import { getCameraMonitoringHistory } from "./installationApi";
import {
  areRotationAnglesInSync,
  computeRotationPreviewDelta,
  getAppliedRotationForCycle,
  normalizeRotationAngle,
  resolveAppliedRotation,
} from "./installationRotationUtils";

export function useInstallationImageRotation({
  routeCameraId,
  side,
  cycleEntries,
  camera,
}) {
  const [monitoringEntries, setMonitoringEntries] = useState([]);
  const [monitoringLoading, setMonitoringLoading] = useState(true);

  const recentCycleEntry = useMemo(
    () => getRecentRawImageEntry(cycleEntries),
    [cycleEntries],
  );

  const targetAngle = normalizeRotationAngle(camera?.image_rotation_angle ?? 0);

  useEffect(() => {
    if (!routeCameraId) {
      setMonitoringEntries([]);
      setMonitoringLoading(false);
      return;
    }

    let cancelled = false;
    setMonitoringLoading(true);

    void getCameraMonitoringHistory(routeCameraId)
      .then((entries) => {
        if (!cancelled) {
          setMonitoringEntries(entries);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMonitoringEntries([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMonitoringLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [routeCameraId]);

  const appliedResult = useMemo(
    () =>
      getAppliedRotationForCycle(
        monitoringEntries,
        recentCycleEntry?.name,
        side,
      ),
    [monitoringEntries, recentCycleEntry?.name, side],
  );

  const { appliedAngle, appliedMatched } = useMemo(
    () => resolveAppliedRotation(appliedResult, targetAngle),
    [appliedResult, targetAngle],
  );

  const previewDelta = useMemo(
    () => computeRotationPreviewDelta(appliedAngle, targetAngle),
    [appliedAngle, targetAngle],
  );

  const isInSync =
    appliedMatched && areRotationAnglesInSync(appliedAngle, targetAngle);

  return {
    recentCycleEntry,
    targetAngle,
    appliedAngle,
    appliedMatched,
    previewDelta,
    isInSync,
    monitoringLoading,
  };
}
